<template>
  <b-overlay :show="formShow" style="height: 20vh; background-color: white;" rounded="sm" no-fade>
    <div v-show="userDataInfo != null" style="font-size: 1.4rem;" class="mt-2 text-center bg-info p-2 ">
      Hello
      <strong>{{ userDataInfo ? userDataInfo.name : 'N/A' }} {{ userDataInfo ? userDataInfo.surname : 'N/A' }}</strong
      >, welcome to Solstice

      <h3 class="muted mt-2">{{ dateNow }}</h3>

      <div class="home-panels">
        <!-- Billing cycle summary: same range as Billing > Create (last month) -->
        <router-link :to="{ name: 'billing-create' }" class="billing-summary mt-2" :class="{ empty: billingCount === 0 }">
          <span class="billing-summary-icon">
            <b-spinner v-if="billingCount === null" small />
            <feather-icon v-else icon="FileTextIcon" size="22" />
          </span>
          <span class="billing-summary-text">
            <template v-if="billingCount === null">Checking this billing cycle…</template>
            <template v-else>
              <strong>{{ billingCount }}</strong> billing {{ billingCount === 1 ? 'record' : 'records' }} in the Create tab this billing cycle
              <small class="d-block">{{ cycleLabel }}<span v-if="billingCount"> · {{ billingSplit }}</span></small>
            </template>
          </span>
          <span class="billing-summary-cta">Click to see <feather-icon icon="ArrowRightIcon" size="16" /></span>
        </router-link>

        <!-- Your own timesheet -->
        <timesheet own-only class="home-timesheet" />
      </div>

    </div>
  </b-overlay>
</template>

<script>
import { BCard, BCardHeader, BCardTitle, BCardText, BCardBody, BRow, BCol, BMedia, BMediaAside, BAvatar, BMediaBody, BLink } from 'bootstrap-vue';
import { kFormatter } from '@core/utils/filter';
import moment from 'moment';
import homeStoreModule from './homeStoreModule';
import store from '@/store';
import { ref, onUnmounted, reactive } from '@vue/composition-api';
import useJwt from '@/auth/jwt/useJwt';
import router from '@/router';
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue';
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue';
import { BSpinner } from 'bootstrap-vue';
import axiosIns from '@/libs/axios';
import Timesheet from '@/views/user/weekly-timesheet/Timesheet.vue';

function ymd(date) {
  return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
}

export default {
  components: {
    BLink,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardText,
    BCardBody,
    BMedia,
    BAvatar,
    BMediaAside,
    BMediaBody,

    AppTimeline,
    AppTimelineItem,
    BSpinner,
    Timesheet,
  },

  setup() {
    const HOME_APP_STORE_MODULE_NAME = 'home';
    // Register module
    if (!store.hasModule(HOME_APP_STORE_MODULE_NAME)) store.registerModule(HOME_APP_STORE_MODULE_NAME, homeStoreModule);
    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(HOME_APP_STORE_MODULE_NAME)) store.unregisterModule(HOME_APP_STORE_MODULE_NAME);
    });
  },

  created() {
    this.checkUser();
    this.loadBillingCount();
  },

  data() {
    const dateNow = moment(new Date()).format('YYYY-MM-DD');

    return {
      dateNow,
      timeLineArray: [],
      formShow: false,
      userDataInfo: null,
      billingCount: null,
      billingCounts: { internal: 0, external: 0 },
    };
  },

  computed: {
    // Billing > Create opens on last month, so "this billing cycle" means the same range.
    cycleRange() {
      const now = new Date();
      return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end: new Date(now.getFullYear(), now.getMonth(), 0),
      };
    },
    cycleLabel() {
      return `${this.cycleRange.start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} billing cycle`;
    },
    billingSplit() {
      return `${this.billingCounts.internal} internal, ${this.billingCounts.external} external`;
    },
  },

  methods: {
    kFormatter,

    async loadBillingCount() {
      const range = { startDate: ymd(this.cycleRange.start), endDate: ymd(this.cycleRange.end) };
      try {
        const [internal, external] = await Promise.all(
          ['internal', 'external'].map((type) => axiosIns.post('getNonInvoicedData', { ...range, type })),
        );
        this.billingCounts = {
          internal: (internal.data.items || []).length,
          external: (external.data.items || []).length,
        };
        this.billingCount = this.billingCounts.internal + this.billingCounts.external;
      } catch (error) {
        console.error(error);
        this.billingCount = 0;
      }
    },

    checkUser() {
      this.formShow = true;

      this.userId = parseInt(this.userData.id);

      store
        .dispatch('home/getUserById', { id: this.userId })
        .then((response) => {
          this.userDataInfo = response.data;

          if (response.data.status != 'active') {
            localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName);
            localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName);
            localStorage.removeItem('userData');
            router.push({ name: 'auth-login' });
          } else {
            this.formShow = false;
          }
        })
        .catch((error) => {});
    },
  },
};
</script>

<style scoped>
/* The greeting panel is centered with large text; keep the panels at normal size. */
.home-panels {
  margin: 1.5rem auto 1rem;
  font-size: 1rem;
  text-align: left;
}

.billing-summary {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border-radius: 10px;
  background: #fff;
  border-left: 5px solid #a51c30;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.08);
  color: #5e5873;
  text-decoration: none;
  transition: box-shadow 0.15s, transform 0.15s;
}

.billing-summary:hover {
  box-shadow: 0 6px 22px rgba(165, 28, 48, 0.18);
  transform: translateY(-1px);
}

.billing-summary:focus-visible {
  outline: 2px solid #a51c30;
  outline-offset: 2px;
}

.billing-summary.empty {
  border-left-color: #28c76f;
}

.billing-summary-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(165, 28, 48, 0.1);
  color: #a51c30;
  flex: none;
}

.billing-summary.empty .billing-summary-icon {
  background: rgba(40, 199, 111, 0.12);
  color: #28c76f;
}

.billing-summary-text {
  font-size: 1.1rem;
  line-height: 1.35;
}

.billing-summary-text strong {
  font-size: 1.4rem;
  color: #a51c30;
}

.billing-summary.empty .billing-summary-text strong {
  color: #28c76f;
}

.billing-summary-text small {
  font-size: 0.85rem;
  color: #82868b;
}

.billing-summary-cta {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #a51c30;
  white-space: nowrap;
}

.home-timesheet {
  margin-top: 0.5rem;
}

.billing-summary.mt-2 {
  margin-top: 0 !important;
}
</style>
